import type { Cell, SheetData } from 'write-excel-file';
import type { Comment, EventSnapshot, PresenterExportComment } from '../types';
import { api } from './api';
import { presenterApi } from './presenterApi';

const headerStyle = {
  backgroundColor: '#1432FF',
  color: '#FFFFFF',
  fontWeight: 'bold' as const,
  alignVertical: 'center' as const,
};

function textCell(value: string, options: Partial<Cell> = {}): Cell {
  return { type: String, value, wrap: true, ...options };
}

function numberCell(value: number): Cell {
  return { type: Number, value, align: 'right' };
}

function headerRow(values: string[]): Cell[] {
  return values.map((value) => textCell(value, headerStyle));
}

export function buildExportSheets(
  snapshot: EventSnapshot,
  commentsByTopic: Map<string, Comment[]>,
  exportedAt: Date,
  scope: 'public' | 'host' = 'public',
): [SheetData, SheetData] {
  const host = scope === 'host';
  const summaryColumns = host ? 11 : 6;
  const summary: SheetData = [
    [textCell(snapshot.settings.eventTitle, { fontSize: 18, fontWeight: 'bold', span: summaryColumns })],
    [textCell(`Exported: ${exportedAt.toLocaleString()}`, { color: '#5D5D61', span: summaryColumns })],
    [],
    headerRow(['Order', 'Topic', 'Presenter / Team', 'Likes', 'Flowers', 'Comments',
      ...(host ? ['Topic ID', 'Published', 'Status', 'Discussion question', 'Discussion options'] : []),
    ]),
    ...snapshot.topics.map((topic) => [
      numberCell(topic.displayOrder),
      textCell(topic.title),
      textCell(topic.presenter),
      numberCell(topic.likeCount),
      numberCell(topic.flowerCount),
      numberCell(topic.commentCount),
      ...(host ? [
        textCell(topic.id), textCell(topic.isPublished ? 'Yes' : 'No'),
        textCell(topic.status), textCell(topic.discussionQuestion ?? ''),
        textCell(topic.discussionOptions.map((option, index) =>
          `${String.fromCharCode(65 + index)}. ${option}`).join('\n')),
      ] : []),
    ]),
  ];

  const comments: SheetData = [
    [textCell(host ? 'All comments' : 'Public comments', { fontSize: 18, fontWeight: 'bold', span: host ? 7 : 6 })],
    [
      textCell(
        host ? 'Includes hidden comments and unpublished topics. Handle this host export with care.'
          : 'Only comments currently visible to participants are included.',
        { color: '#5D5D61', span: host ? 7 : 6 },
      ),
    ],
    [],
    headerRow([
      'Topic order',
      'Topic',
      'Presenter / Team',
      'Display name',
      'Comment',
      'Posted at',
      ...(host ? ['Visibility'] : []),
    ]),
    ...snapshot.topics.flatMap((topic) =>
      (commentsByTopic.get(topic.id) ?? []).map((comment) => [
        numberCell(topic.displayOrder),
        textCell(topic.title),
        textCell(topic.presenter),
        textCell(comment.nickname),
        textCell(comment.body),
        {
          type: Date,
          value: new Date(comment.createdAt),
          format: 'yyyy-mm-dd hh:mm',
        } satisfies Cell,
        ...(host ? [textCell(commentVisibility(comment))] : []),
      ]),
    ),
  ];

  return [summary, comments];
}

function commentVisibility(comment: Comment): string {
  if (!('isHidden' in comment) || typeof comment.isHidden !== 'boolean') {
    throw new Error('Host export comment visibility is unavailable.');
  }
  return comment.isHidden ? 'Hidden' : 'Public';
}

async function downloadResults(
  sheets: [SheetData, SheetData],
  scope: 'public' | 'host',
  exportedAt: Date,
): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file');
  const host = scope === 'host';
  const stamp = host
    ? exportedAt.toISOString().replace(/[:.]/g, '-')
    : exportedAt.toISOString().slice(0, 10);
  await writeXlsxFile(sheets, {
    sheets: ['Topic Results', host ? 'All Comments' : 'Public Comments'],
    columns: [
      [
        { width: 10 }, { width: 52 }, { width: 28 }, { width: 12 }, { width: 12 }, { width: 12 },
        ...(host ? [{ width: 38 }, { width: 12 }, { width: 12 }, { width: 70 }, { width: 70 }] : []),
      ],
      [
        { width: 12 }, { width: 52 }, { width: 28 }, { width: 18 }, { width: 70 }, { width: 22 },
        ...(host ? [{ width: 12 }] : []),
      ],
    ],
    stickyRowsCount: 4,
    fontFamily: 'Aptos',
    fontSize: 11,
    fileName: `Panther-Meeting-2026-${host ? 'host-' : ''}results-${stamp}.xlsx`,
  });
}

export async function exportPresenterResults(): Promise<void> {
  const result = await presenterApi.export();
  const commentsByTopic = new Map<string, PresenterExportComment[]>();
  for (const comment of result.comments) {
    const comments = commentsByTopic.get(comment.topicId) ?? [];
    comments.push(comment);
    commentsByTopic.set(comment.topicId, comments);
  }
  const exportedAt = new Date(result.exportedAt);
  await downloadResults(
    buildExportSheets(result.snapshot, commentsByTopic, exportedAt, 'host'),
    'host', exportedAt,
  );
}

export async function exportEventResults(snapshot: EventSnapshot): Promise<void> {
  const comments = await Promise.all(
    snapshot.topics.map(async (topic) => [
      topic.id,
      await api.getComments(topic.id),
    ] as const),
  );
  const exportedAt = new Date();
  await downloadResults(buildExportSheets(snapshot, new Map(comments), exportedAt), 'public', exportedAt);
}
